import React, { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import {
  getMyLeaveBalance, getAttendance, getExpenses,
  getMySlips, getHolidays, getMyReferralLink, getLeaveRequests,
} from "../api";
import { useAuth } from "../context/AuthContext";
import { useTenantTimezone } from "../hooks/useTenantTimezone";
import { formatDate, tenantHour } from "../utils/dateFormat";

const NAVY = "#1e3a8a";
const TEAL = "#00c9a7";

export default function EmployeeDashboard() {
  const { user } = useAuth();
  const tz = useTenantTimezone();
  const nav = useNavigate();
  const [referralCopied, setReferralCopied] = useState(false);

  const now        = new Date();
  const month      = now.getMonth() + 1;
  const year       = now.getFullYear();
  const dateStr    = formatDate(now, "long", tz);
  const hour       = tenantHour(tz);
  const greeting   = hour < 12 ? "Good morning" : hour < 17 ? "Good afternoon" : "Good evening";

  // Queries
  const { data: balances = [] } = useQuery({ queryKey: ["my-balance"], queryFn: () => getMyLeaveBalance() });
  const { data: attendance = [] } = useQuery({ queryKey: ["my-attendance", month, year], queryFn: () => getAttendance({ month, year }) });
  const { data: expenses = [] } = useQuery({ queryKey: ["expenses"], queryFn: () => getExpenses() });
  const { data: slips = [] } = useQuery({ queryKey: ["my-slips"], queryFn: getMySlips });
  const { data: holidays = [] } = useQuery({ queryKey: ["holidays", year], queryFn: () => getHolidays(year) });
  const { data: leaveRequests = [] } = useQuery({ queryKey: ["my-requests"], queryFn: () => getLeaveRequests({ mine: true }) });
  const { data: referral } = useQuery({ queryKey: ["my-referral-link"], queryFn: getMyReferralLink, retry: false });

  // Computed stats
  const totalLeaveRemaining = (balances as any[]).reduce((sum, b) => sum + (b.available || 0), 0);
  const attRecords = attendance as any[];
  const presentDays = attRecords.filter(r => ["present", "work_from_home", "wfh"].includes(r.status)).length;
  const pendingExpenses = (expenses as any[]).filter(e => e.status === "pending").length;
  const latestSlip = (slips as any[])[0];
  const today = now.toISOString().split("T")[0];
  const upcomingHolidays = (holidays as any[]).filter(h => h.date >= today).slice(0, 4);
  const recentLeaves = (leaveRequests as any[]).slice(0, 3);
  const pendingLeaves = (leaveRequests as any[]).filter(r => r.status === "pending").length;

  function copyReferral() {
    if (referral?.link) {
      navigator.clipboard.writeText(referral.link).then(() => {
        setReferralCopied(true);
        setTimeout(() => setReferralCopied(false), 3000);
      });
    }
  }

  const LEAVE_STATUS_COLORS: Record<string, { bg: string; text: string }> = {
    pending:  { bg: "#fef9c3", text: "#854d0e" },
    approved: { bg: "#dcfce7", text: "#166534" },
    rejected: { bg: "#fee2e2", text: "#991b1b" },
    cancelled:{ bg: "#f1f5f9", text: "#64748b" },
  };

  return (
    <div style={{ maxWidth: 900, margin: "0 auto" }}>

      {/* ── Greeting ── */}
      <div style={{ marginBottom: 24 }}>
        <h1 style={{ margin: "0 0 4px", fontSize: 26, fontWeight: 800, color: "#0f172a" }}>
          {greeting}, {(user?.name || "").split(" ")[0]} 👋
        </h1>
        <p style={{ margin: 0, fontSize: 13, color: "#94a3b8" }}>{dateStr}</p>
      </div>

      {/* ── Stats row ── */}
      <div className="stat-row" style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 12, marginBottom: 20 }}>

        <StatCard
          label="Leave Balance"
          value={`${totalLeaveRemaining.toFixed(1)}`}
          unit="days left"
          color={NAVY}
          icon="🏖️"
          onClick={() => nav("/my-leaves")}
          sub={pendingLeaves > 0 ? `${pendingLeaves} pending` : undefined}
        />

        <StatCard
          label="Attendance"
          value={String(presentDays)}
          unit={`days this month`}
          color="#0f766e"
          icon="✅"
          onClick={() => nav("/attendance")}
          sub={attRecords.length > 0 ? `${attRecords.length} marked` : "Not marked yet"}
        />

        <StatCard
          label="Expenses"
          value={String(pendingExpenses)}
          unit="pending claims"
          color={pendingExpenses > 0 ? "#d97706" : "#16a34a"}
          icon="🧾"
          onClick={() => nav("/expenses")}
          sub={pendingExpenses === 0 ? "All settled" : "Awaiting approval"}
        />

        <StatCard
          label="Last Payslip"
          value={latestSlip ? `${latestSlip.currency_symbol || ""}${Number(latestSlip.net_salary || 0).toLocaleString()}` : "—"}
          unit={latestSlip ? `${latestSlip.month_name} ${latestSlip.year}` : "No slip yet"}
          color="#7c3aed"
          icon="💰"
          onClick={() => nav("/my-slips")}
        />

      </div>

      {/* ── Main grid ── */}
      <div className="grid-2col" style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}>

        {/* Quick Actions */}
        <div style={card}>
          <SectionTitle>Quick Actions</SectionTitle>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
            {[
              { icon: "🏖️", label: "Apply Leave",     path: "/my-leaves",  color: NAVY },
              { icon: "📅", label: "Mark Attendance", path: "/attendance",  color: "#0f766e" },
              { icon: "🧾", label: "Submit Expense",  path: "/expenses",   color: "#d97706" },
              { icon: "💰", label: "View Payslips",   path: "/my-slips",   color: "#7c3aed" },
              { icon: "📦", label: "My Assets",       path: "/assets",     color: "#475569" },
              { icon: "🚪", label: "Offboarding",     path: "/exit",       color: "#dc2626" },
            ].map(a => (
              <button key={a.path} onClick={() => nav(a.path)}
                style={{
                  display: "flex", alignItems: "center", gap: 8,
                  padding: "10px 12px", borderRadius: 10,
                  background: a.color + "10", border: `1px solid ${a.color}25`,
                  cursor: "pointer", textAlign: "left",
                  transition: "background 0.15s",
                }}
                onMouseEnter={e => (e.currentTarget.style.background = a.color + "20")}
                onMouseLeave={e => (e.currentTarget.style.background = a.color + "10")}
              >
                <span style={{ fontSize: 18 }}>{a.icon}</span>
                <span style={{ fontSize: 12, fontWeight: 600, color: a.color }}>{a.label}</span>
              </button>
            ))}
          </div>
        </div>

        {/* Upcoming Holidays */}
        <div style={card}>
          <SectionTitle>Upcoming Holidays</SectionTitle>
          {upcomingHolidays.length === 0 ? (
            <div style={{ color: "#94a3b8", fontSize: 13, padding: "12px 0" }}>No upcoming holidays.</div>
          ) : (
            <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
              {upcomingHolidays.map((h: any) => {
                const d = new Date(h.date);
                const daysAway = Math.ceil((d.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));
                return (
                  <div key={h.id} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "8px 12px", background: "#f8fafc", borderRadius: 8, border: "1px solid #f1f5f9" }}>
                    <div>
                      <div style={{ fontSize: 13, fontWeight: 600, color: "#0f172a" }}>{h.name}</div>
                      <div style={{ fontSize: 11, color: "#94a3b8" }}>
                        {formatDate(h.date)}
                        {h.location && <span style={{ marginLeft: 6, background: "#f1f5f9", borderRadius: 4, padding: "1px 5px" }}>{h.location}</span>}
                      </div>
                    </div>
                    <span style={{
                      fontSize: 11, fontWeight: 700,
                      background: daysAway <= 7 ? "#fef9c3" : "#eff6ff",
                      color: daysAway <= 7 ? "#854d0e" : "#1d4ed8",
                      border: `1px solid ${daysAway <= 7 ? "#fde047" : "#bfdbfe"}`,
                      padding: "2px 8px", borderRadius: 20,
                    }}>
                      {daysAway === 0 ? "Today" : daysAway === 1 ? "Tomorrow" : `${daysAway}d`}
                    </span>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Recent Leave Requests */}
        <div style={card}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 14 }}>
            <SectionTitle noMargin>My Leave Requests</SectionTitle>
            <button onClick={() => nav("/my-leaves")} style={{ background: "none", border: "none", fontSize: 12, color: NAVY, cursor: "pointer", fontWeight: 600 }}>View all →</button>
          </div>
          {recentLeaves.length === 0 ? (
            <div style={{ color: "#94a3b8", fontSize: 13, padding: "8px 0" }}>No leave requests yet.</div>
          ) : (
            <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
              {recentLeaves.map((r: any) => {
                const sc = LEAVE_STATUS_COLORS[r.status] || { bg: "#f1f5f9", text: "#475569" };
                return (
                  <div key={r.id} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "8px 12px", background: "#f8fafc", borderRadius: 8, border: "1px solid #f1f5f9" }}>
                    <div>
                      <div style={{ fontSize: 13, fontWeight: 600, color: "#0f172a" }}>{r.leave_type}</div>
                      <div style={{ fontSize: 11, color: "#94a3b8" }}>{r.start_date} → {r.end_date} · {r.days}d</div>
                    </div>
                    <span style={{ background: sc.bg, color: sc.text, fontSize: 11, fontWeight: 600, padding: "2px 8px", borderRadius: 20, textTransform: "capitalize" }}>{r.status}</span>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Leave Balance summary */}
        <div style={card}>
          <SectionTitle>Leave Balance {year}</SectionTitle>
          {(balances as any[]).length === 0 ? (
            <div style={{ color: "#94a3b8", fontSize: 13 }}>No leave types configured.</div>
          ) : (
            <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
              {(balances as any[]).filter(b => !b.is_comp_off || b.available > 0).map((b: any) => {
                const pct = b.allocated > 0 ? Math.min(100, Math.round((b.used / b.allocated) * 100)) : 0;
                return (
                  <div key={b.leave_type_id}>
                    <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 4 }}>
                      <span style={{ fontSize: 12, fontWeight: 600, color: "#334155" }}>{b.leave_type_name}</span>
                      <span style={{ fontSize: 12, color: "#64748b" }}>
                        <strong style={{ color: b.available > 0 ? "#0f172a" : "#dc2626" }}>{b.available.toFixed(1)}</strong> / {(b.allocated + b.carried_forward).toFixed(1)} days
                      </span>
                    </div>
                    <div style={{ background: "#f1f5f9", borderRadius: 4, height: 6, overflow: "hidden" }}>
                      <div style={{ width: `${pct}%`, height: "100%", background: pct >= 90 ? "#dc2626" : pct >= 70 ? "#d97706" : b.color || TEAL, borderRadius: 4, transition: "width 0.4s" }} />
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

      </div>

      {/* ── Refer a Friend ── */}
      {referral?.link && (
        <div style={{ marginTop: 16, background: `linear-gradient(135deg, ${NAVY}08, ${TEAL}10)`, border: `1px solid ${TEAL}40`, borderRadius: 14, padding: "20px 24px" }}>
          <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 16, flexWrap: "wrap" }}>
            <div style={{ flex: 1, minWidth: 220 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 6 }}>
                <span style={{ fontSize: 22 }}>👥</span>
                <span style={{ fontSize: 16, fontWeight: 700, color: NAVY }}>Refer a Friend</span>
              </div>
              <p style={{ margin: "0 0 10px", fontSize: 13, color: "#475569", lineHeight: 1.5 }}>
                Know someone great? Share your personal link — if they apply via your link, they'll be tagged as your referral.
              </p>
              <div style={{ display: "flex", alignItems: "center", gap: 8, background: "white", border: "1px solid #e2e8f0", borderRadius: 8, padding: "8px 12px" }}>
                <span style={{ fontSize: 12, color: "#64748b", fontFamily: "monospace", flex: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                  {referral.link}
                </span>
              </div>
            </div>
            <div style={{ display: "flex", flexDirection: "column", gap: 8, flexShrink: 0 }}>
              <button onClick={copyReferral}
                style={{
                  background: referralCopied ? "#dcfce7" : `linear-gradient(135deg, ${NAVY}, ${TEAL})`,
                  color: referralCopied ? "#166534" : "white",
                  border: referralCopied ? "1px solid #86efac" : "none",
                  borderRadius: 9, padding: "10px 22px",
                  fontSize: 13, fontWeight: 700, cursor: "pointer", whiteSpace: "nowrap",
                }}>
                {referralCopied ? "✓ Copied!" : "🔗 Copy My Link"}
              </button>
              <button
                onClick={() => {
                  if (navigator.share) {
                    navigator.share({ title: "We're hiring!", url: referral.link });
                  } else {
                    copyReferral();
                  }
                }}
                style={{ background: "white", border: "1px solid #e2e8f0", borderRadius: 9, padding: "8px 22px", fontSize: 13, fontWeight: 600, cursor: "pointer", color: "#475569" }}>
                📤 Share
              </button>
            </div>
          </div>
        </div>
      )}

    </div>
  );
}

// ── Sub-components ────────────────────────────────────────────────────────────

function StatCard({ label, value, unit, color, icon, onClick, sub }: {
  label: string; value: string; unit: string; color: string;
  icon: string; onClick?: () => void; sub?: string;
}) {
  return (
    <div onClick={onClick}
      style={{
        background: "white", border: "1px solid #e2e8f0", borderRadius: 12,
        padding: "16px 18px", cursor: onClick ? "pointer" : "default",
        boxShadow: "0 1px 3px rgba(0,0,0,0.04)", transition: "box-shadow 0.15s, transform 0.1s",
        borderLeft: `3px solid ${color}`,
      }}
      onMouseEnter={e => { if (onClick) { e.currentTarget.style.boxShadow = "0 4px 12px rgba(0,0,0,0.08)"; e.currentTarget.style.transform = "translateY(-1px)"; } }}
      onMouseLeave={e => { e.currentTarget.style.boxShadow = "0 1px 3px rgba(0,0,0,0.04)"; e.currentTarget.style.transform = "translateY(0)"; }}
    >
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
        <div>
          <div style={{ fontSize: 11, fontWeight: 600, color: "#94a3b8", textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: 6 }}>{label}</div>
          <div style={{ fontSize: 24, fontWeight: 800, color, lineHeight: 1 }}>{value}</div>
          <div style={{ fontSize: 11, color: "#64748b", marginTop: 4 }}>{unit}</div>
          {sub && <div style={{ fontSize: 10, color: "#94a3b8", marginTop: 3 }}>{sub}</div>}
        </div>
        <span style={{ fontSize: 24, opacity: 0.8 }}>{icon}</span>
      </div>
    </div>
  );
}

function SectionTitle({ children, noMargin }: { children?: React.ReactNode; noMargin?: boolean }) {
  return (
    <div style={{ fontSize: 13, fontWeight: 700, color: "#0f172a", marginBottom: noMargin ? 0 : 14, paddingBottom: noMargin ? 0 : 10, borderBottom: noMargin ? "none" : "1px solid #f1f5f9" }}>
      {children}
    </div>
  );
}

const card: React.CSSProperties = {
  background: "white", border: "1px solid #e2e8f0", borderRadius: 12,
  padding: "18px 20px", boxShadow: "0 1px 3px rgba(0,0,0,0.04)",
};
